import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    DashboardPeriod,
    FullDashboardResponse,
    getFullDashboard,
} from '@/lib/api/dashboard/dashboard-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useState } from 'react'

// Dashboard components
import DashboardHeader from './components/dashboard-header'
import PeriodSelector from './components/period-selector'
import QuickStats from './components/quick-stats'

// Charts
import AttendancesChart from './components/charts/attendances-chart'
import ClientsGrowthChart from './components/charts/clients-growth-chart'
import RevenueChart from './components/charts/revenue-chart'

// Health charts
import ConsultationsChart from './components/charts/health/consultations-chart'
import TopProfessionalsChart from './components/charts/health/top-professionals-chart'
import TriageUrgencyChart from './components/charts/health/triage-urgency-chart'

// Widgets
import PendingActionsWidget from './components/widgets/pending-actions-widget'
import QuickActionsWidget from './components/widgets/quick-actions-widget'
import SubscriptionWidget from './components/widgets/subscription-widget'
import UpcomingEventsWidget from './components/widgets/upcoming-events-widget'

// Tables
import RecentClientsTable from './components/tables/recent-clients-table'
import RecentConsultationsTable from './components/tables/recent-consultations-table'
import RecentTriagesTable from './components/tables/recent-triages-table'

export default function DashboardPage() {
    const { user } = useAuth()
    const serviceLabels = useServiceLabel()
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [dashboardData, setDashboardData] = useState<FullDashboardResponse | null>(null)
    const [period, setPeriod] = useState<DashboardPeriod>('month')
    const [customStart, setCustomStart] = useState<string>('')
    const [customEnd, setCustomEnd] = useState<string>('')
    const [error, setError] = useState<string | null>(null)

    const loadDashboard = useCallback(async (isRefresh = false) => {
        if (!user?.companyId) return

        // Para periodo custom, so carrega quando as duas datas estao preenchidas.
        if (period === 'custom' && (!customStart || !customEnd)) {
            setLoading(false)
            return
        }

        if (isRefresh) {
            setRefreshing(true)
        } else {
            setLoading(true)
        }
        setError(null)
        try {
            const response = await getFullDashboard({
                period,
                startDate: period === 'custom' ? customStart : undefined,
                endDate: period === 'custom' ? customEnd : undefined,
            })

            if (response.success) {
                setDashboardData(response.result)
            } else {
                setError(response.error || 'Erro ao carregar dashboard')
                console.error('Erro ao carregar dashboard:', response.error)
            }
        } catch (err) {
            setError('Erro ao carregar dashboard')
            console.error('Erro ao carregar dashboard:', err)
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [user?.companyId, period, customStart, customEnd])

    useEffect(() => {
        if (user?.companyId) {
            loadDashboard()
        }
    }, [user?.companyId, loadDashboard])

    const handlePeriodChange = (newPeriod: DashboardPeriod) => {
        setPeriod(newPeriod)
    }

    const handleCustomDatesChange = (start: string, end: string) => {
        setCustomStart(start)
        setCustomEnd(end)
    }

    const handleRefresh = () => {
        loadDashboard(true)
    }

    const isHealth = dashboardData?.segment === 'HEALTH'
    const isIntermediation = dashboardData?.secondarySegment === 'INTERMEDIATION'

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="loading loading-spinner loading-lg text-secondary"></span>
                <p className="text-base-content/60 text-sm">
                    Carregando dashboard...
                </p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="text-error text-lg">Erro ao carregar dashboard</div>
                <p className="text-base-content/60 text-sm">{error}</p>
                <button className="btn btn-secondary btn-sm" onClick={handleRefresh}>
                    Tentar novamente
                </button>
            </div>
        )
    }

    if (!dashboardData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-base-content/60 text-sm">
                    Nenhum dado disponível
                </p>
            </div>
        )
    }

    const { stats, charts, widgets, tables } = dashboardData

    // Safety checks for optional data
    const safeCharts = charts || {
        attendancesByPeriod: [],
        revenueByPeriod: [],
        clientsByPeriod: [],
        ordersByStatus: [],
        paymentsByType: [],
        topProducts: [],
    }
    const safeWidgets = widgets || {
        upcomingEvents: [],
        pendingActions: [],
        subscription: {
            planName: 'Sem plano',
            status: 'INACTIVE',
        },
        recentActivity: [],
    }
    const safeTables = tables || {
        recentClients: [],
    }

    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-500">
            {/* Header */}
            <DashboardHeader
                lastUpdated={dashboardData.lastUpdated}
                refreshing={refreshing}
                onRefresh={handleRefresh}
                humanResponseAvgSeconds={stats.humanResponseAvgSeconds}
                aiResponseAvgSeconds={stats.aiResponseAvgSeconds}
                humanResponseCount={stats.humanResponseCount}
                aiResponseCount={stats.aiResponseCount}
            />

            {/* Period Selector */}
            <PeriodSelector
                value={period}
                onChange={handlePeriodChange}
                startDate={customStart}
                endDate={customEnd}
                onCustomDatesChange={handleCustomDatesChange}
            />

            {/* Pending Actions Alert */}
            {safeWidgets.pendingActions.length > 0 && (
                <PendingActionsWidget actions={safeWidgets.pendingActions} />
            )}

            {/* Quick Stats */}
            <QuickStats
                stats={stats}
                isHealth={isHealth}
                isIntermediation={isIntermediation}
                period={period}
            />

            {/* Main Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AttendancesChart data={safeCharts.attendancesByPeriod || []} period={period} />
                <RevenueChart data={safeCharts.revenueByPeriod || []} period={period} />
            </div>

            {/* Secondary Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ClientsGrowthChart data={safeCharts.clientsByPeriod || []} />
            </div>

            {/* Health-specific Charts */}
            {isHealth && safeCharts.health && (
                <>
                    <div className="divider">
                        <span className="text-sm text-base-content/60 font-medium">
                            Métricas de Saúde
                        </span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ConsultationsChart data={safeCharts.health.consultationsByPeriod || []} period={period} />
                        <TopProfessionalsChart
                            data={stats.health?.topProfessionals || []}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {!isIntermediation && (
                            <TriageUrgencyChart data={safeCharts.health.triagesByUrgency || []} />
                        )}
                        <div
                            className={`bg-base-100 rounded-xl border border-base-300/50 p-5 ${
                                isIntermediation ? 'md:col-span-2' : ''
                            }`}
                        >
                            <h3 className="text-lg font-semibold text-base-content mb-4">
                                Resumo de Saúde
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-info/10 rounded-lg p-4">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        {serviceLabels.Consultations} no período
                                    </p>
                                    <p className="text-2xl font-bold text-info">
                                        {stats.health?.consultationsPeriod || 0}
                                    </p>
                                </div>
                                <div className="bg-accent/10 rounded-lg p-4">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        {serviceLabels.Orders} no período
                                    </p>
                                    <p className="text-2xl font-bold text-accent">
                                        {stats.health?.ordersPeriod || 0}
                                    </p>
                                </div>
                                <div className="bg-success/10 rounded-lg p-4">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Exames no período
                                    </p>
                                    <p className="text-2xl font-bold text-success">
                                        {stats.health?.examsPeriod || 0}
                                    </p>
                                </div>
                                {!isIntermediation && (
                                    <div className="bg-warning/10 rounded-lg p-4">
                                        <p className="text-xs text-base-content/60 mb-1">
                                            Triagens no período
                                        </p>
                                        <p className="text-2xl font-bold text-warning">
                                            {stats.health?.triagesPeriod || 0}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Widgets Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <UpcomingEventsWidget events={safeWidgets.upcomingEvents || []} isHealth={isHealth} />
                <SubscriptionWidget subscription={safeWidgets.subscription} />
                <QuickActionsWidget isHealth={isHealth} />
            </div>

            {/* Tables Section */}
            <div className="divider">
                <span className="text-sm text-base-content/60 font-medium">
                    Atividade Recente
                </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RecentClientsTable clients={safeTables.recentClients || []} />
            </div>

            {/* Health-specific Tables */}
            {isHealth && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {safeTables.recentConsultations && safeTables.recentConsultations.length > 0 && (
                        <RecentConsultationsTable
                            consultations={safeTables.recentConsultations}
                        />
                    )}
                    {safeTables.recentTriages && safeTables.recentTriages.length > 0 && (
                        <RecentTriagesTable triages={safeTables.recentTriages} />
                    )}
                </div>
            )}
        </div>
    )
}
